import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Kachel, Knopf } from "../design/Kachel";
import { Abweisung, Status } from "../design/Hinweis";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { gemerkteReviere, idToken, useSitzung } from "../plattform/sitzung";

/**
 * Revier (oberflaeche.md §7 „Reviere / Revier“): Verwaltung des Reviers durch die Jagdherrin — Teilnehmerübersicht,
 * Stände, Revierregeln, Sicherheitsbestimmungen, Notfallkontakte, Rettungspunkte, Revierkarte, Exportieren und
 * Löschen (`alertdialog`); dazu die Revierkennung (RevierId) mit „Kopieren“ und „Teilen“, die die Jagdherrin anderen
 * Jagdherren selbst weitergibt (Jagdherrenkanal, Beteiligung; oberflaeche.md §7 V-18, Anforderungen §7a K26, K88 b).
 */
export function useReviername(revierId: string | undefined): string | null {
  const { uid } = useSitzung();
  const r = useDokument(uid && revierId ? `personen/${uid}/reviere/${revierId}` : null);
  return (r.daten?.["Revier"] as string | undefined) ?? gemerkteReviere(uid).find((g) => g.Revier === revierId)?.Reviername ?? null;
}

const ZIELE: ReadonlyArray<[string, string, string]> = [
  ["teilnehmer", "Teilnehmerübersicht", "teilnehmer"],
  ["staende", "Stände", "stand"],
  ["revierregeln", "Revierregeln", "einstellungen"],
  ["sicherheitsbestimmungen", "Sicherheitsbestimmungen", "einweisung"],
  ["notfallkontakte", "Notfallkontakte", "notruf"],
  ["rettungspunkte", "Rettungspunkte", "rettungspunkt"],
  ["revierkarte", "Revierkarte", "karte"],
];

const WARTEN_MS = 1000;
const HOECHSTENS_VERSUCHE = 60;

/**
 * Lädt eine Datei des Reviers über `GET /api/dateien/<DateiId>` (medien.md §7), sobald der Worker sie erstellt hat
 * (`datei-nicht-bereit`), und übergibt sie dem Gerät zum Speichern.
 */
async function dateiHerunterladen(datei: string): Promise<string | null> {
  for (let versuch = 0; versuch < HOECHSTENS_VERSUCHE; versuch++) {
    const token = await idToken();
    const r = await fetch(`/api/dateien/${encodeURIComponent(datei)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .catch(() => null);
    if (r?.status === 200) {
      const name = /filename="([^"]+)"/.exec(r.headers.get("Content-Disposition") ?? "")?.[1] ?? "export.zip";
      const adresse = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = adresse;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(adresse), WARTEN_MS * 10);
      return null;
    }
    if (r && r.status !== 409) return ((await r.json().catch(() => ({}))) as { grund?: string }).grund ?? "kein-zugriff";
    await new Promise((weiter) => setTimeout(weiter, WARTEN_MS));
  }
  return "datei-nicht-bereit";
}

export function Revier() {
  const { revierId = "" } = useParams();
  const navigate = useNavigate();
  const name = useReviername(revierId);
  const [grund, setGrund] = useState<string | null>(null);
  const [exportLaeuft, setExportLaeuft] = useState(false);
  const [exportFertig, setExportFertig] = useState(false);
  const [loeschen, setLoeschen] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  async function kennungKopieren() {
    await navigator.clipboard.writeText(revierId);
    setKopiert(true);
  }

  async function kennungTeilen() {
    try {
      await navigator.share({ title: `Revierkennung ${name ?? ""}`.trim(), text: revierId });
    } catch {
      // abgebrochen oder ohne Teilen-Funktion: nichts zu tun
    }
  }

  async function exportieren() {
    setGrund(null);
    setExportFertig(false);
    setExportLaeuft(true);
    const a = await befehl("RevierExportAnfordern", { Revier: revierId });
    const fehler = a.ok && a.kennungen["Export"] ? await dateiHerunterladen(a.kennungen["Export"]) : (a.grund ?? "unzulaessige-angaben");
    setExportLaeuft(false);
    if (fehler) setGrund(fehler);
    else setExportFertig(true);
  }

  async function endgueltigLoeschen() {
    setLoeschen(false);
    setGrund(null);
    const a = await befehl("RevierLoeschen", { Revier: revierId });
    if (a.ok) navigate("/reviere", { replace: true });
    else setGrund(abweisungsgrund(a));
  }

  return (
    <Ansicht name={name ?? "Revier"} symbol="karte" unterzeile="Revier">
      <div className="gruppe">
        {ZIELE.map(([pfad, begriff, symbol]) => (
          <Kachel key={pfad} art="quer" symbol={symbol} begriff={begriff} onPress={() => navigate(`/reviere/${revierId}/${pfad}`)} />
        ))}
      </div>
      <section className="liste-bereich platte" role="region" aria-label="Revierkennung">
        <h2 className="kopf"><Symbol name="link" />Revierkennung</h2>
        <p className="linkfeld">{revierId}</p>
        <div className="knopfreihe">
          <Knopf art="planung-zweit" symbol="dokument" onPress={() => void kennungKopieren()}>Kopieren</Knopf>
          <Knopf art="planung-zweit" symbol="link" onPress={() => void kennungTeilen()}>Teilen</Knopf>
        </div>
        {kopiert ? <Status symbol="haken">Kopiert</Status> : null}
      </section>
      <div className="knopfreihe">
        <Knopf art="planung-zweit" symbol="herunterladen" onPress={exportieren} isDisabled={exportLaeuft}>Exportieren</Knopf>
        <Knopf art="planung-zweit" symbol="schliessen" onPress={() => setLoeschen(true)}>Löschen</Knopf>
      </div>
      {exportLaeuft ? <Status symbol="uhr">Export wird erstellt</Status> : null}
      {exportFertig ? <Status symbol="haken">Export heruntergeladen</Status> : null}
      <Abweisung grund={grund} />
      <Dialog name="Löschen" offen={loeschen} bestaetigung onSchliessen={() => setLoeschen(false)}>
        <p>{`Revier ${name ?? ""} endgültig löschen? Alle personenbezogenen Daten des Reviers werden unwiderruflich anonymisiert, seine Dateien gelöscht. Konten der Teilnehmer und ihre anderen Reviere bleiben unberührt.`}</p>
        <div className="knopfreihe">
          <Knopf onPress={endgueltigLoeschen}>Bestätigen</Knopf>
          <Knopf art="zweit" onPress={() => setLoeschen(false)}>Abbrechen</Knopf>
        </div>
      </Dialog>
    </Ansicht>
  );
}
